import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    asyncDeleteOrderAction,
    asyncUpdateOrderAction,
    asyncGetOrdersAction,
    asyncGetOrderAction,
    asyncGetGlobalUnreadCountAction
} from "./ordersAction";
import { IOrder } from "../../../helpers/types";
import { toast } from "react-toastify";
import { tostSetting } from "../../../helpers/scripts";

interface ordersState {
    orders: IOrder[];
    totalOrders: number;
    order: IOrder | null;
    codes: string[];
    currentCode: string;
    currentPage: number;
    selectedCount: number;
    isLoading: boolean;
    error: string;
    statusFilter: string;
    currencyFilter: string;
    msgStatusFilter: string;
    globalUnreadCount: number;
}

const initialState: ordersState = {
    orders: [],
    codes: [],
    currentCode: "",
    totalOrders: 0,
    order: null,
    currentPage: 1,
    selectedCount: 0,
    isLoading: false,
    error: '',
    statusFilter: 'ALL',
    currencyFilter: 'ALL',
    msgStatusFilter: 'ALL',
    globalUnreadCount: 0
};

const ordersSlice = createSlice({
    name: 'ordersSlice',
    initialState,
    reducers: {
        setStatusFilter(state, action: PayloadAction<string>) {
            state.statusFilter = action.payload;
        },
        setCurrencyFilter(state, action: PayloadAction<string>) {
            state.currencyFilter = action.payload;
        },
        setMsgStatusFilter(state, action: PayloadAction<string>) {
            state.msgStatusFilter = action.payload;
        },
        updateOrder(state, action) {
            // Обновляем текущий открытый заказ (OrderPage)
            if (state.order) {
                state.order = {
                    ...state.order,
                    ...action.payload // <-- Теперь мы перезаписываем ВСЕ измененные поля
                };
            } else {
                state.order = action.payload;
            }

            // Также обновляем заказ в общем списке, если админ находится на странице со списком
            const index = state.orders.findIndex(o => o.id === action.payload.id);
            if (index !== -1) {
                state.orders[index] = {
                    ...state.orders[index],
                    ...action.payload
                };
            }
        },
        setCurrentCode(state, action) {
            state.currentCode = action.payload;
        },
        setSelectedOrders(state, action) {
            const selectedList = action.payload;
            state.orders = state.orders.map(item => {
                if (selectedList?.includes(item.tg_user as string)) {
                    return { ...item, isSelected: true }
                } else {
                    return { ...item, isSelected: false }
                }
            });
        },
        setSelectedCount(state, action) {
            state.selectedCount = action.payload;
        },
        setCurrentPageOrders(state, action) {
            state.currentPage = action.payload;
        },
        resetOrders(state) {
            state = initialState; // Внимание: мутация state напрямую тут не сработает в Redux Toolkit (нужно Object.assign или return initialState), но я оставляю твой код как был, чтобы не сломать другую логику. Правильнее: return initialState;
        },
        incrementUnreadCount(state, action: PayloadAction<number>) {
            const orderId = action.payload;
            const order = state.orders.find(o => o.id === orderId);
            if (order) {
                order.unreadCount = (Number(order.unreadCount) || 0) + 1;
            }
        },
        clearUnreadCount(state, action: PayloadAction<number>) {
            const orderId = action.payload;
            const order = state.orders.find(o => o.id === orderId);
            if (order) {
                order.unreadCount = 0;
            }
        },
        setGlobalUnreadCount(state, action: PayloadAction<number>) {
            state.globalUnreadCount = action.payload;
        },
        incrementGlobalUnreadCount(state) {
            state.globalUnreadCount += 1;
        },
        decreaseGlobalUnreadCount(state, action: PayloadAction<number>) {
            state.globalUnreadCount = Math.max(0, state.globalUnreadCount - action.payload);
        },
        addNewOrder(state, action: PayloadAction<any>) {
            const newOrder = action.payload;

            const matchCurrency = state.currencyFilter === 'ALL' || state.currencyFilter === newOrder.type_to;
            const matchStatus = state.statusFilter === 'ALL' || Number(state.statusFilter) === Number(newOrder.status);
            const matchMsgStatus = state.msgStatusFilter === 'ALL' || state.msgStatusFilter === 'WAITING';

            if (matchCurrency && matchStatus && matchMsgStatus) {
                state.totalOrders += 1;

                if (state.currentPage === 1) {
                    state.orders.unshift(newOrder);
                }
            }
        }
    },
    extraReducers: (builder: any) => {
        builder
            .addCase(asyncGetOrdersAction.fulfilled, (state: any, action: PayloadAction<any>) => {
                const { rows, count } = action.payload;
                state.isLoading = false;
                state.error = '';
                state.totalOrders = count;
                const oldItems: string = localStorage.getItem('selectedEmail') ?? '';
                let oldArraySelected: string[] = oldItems ? oldItems?.split(',') : [];
                state.orders = rows ? rows.map((item: any) => {
                    if (oldArraySelected?.includes(item.email as string)) {
                        return { ...item, isSelected: true }
                    } else {
                        return { ...item, isSelected: false }
                    }
                }) : [];
            })
            .addCase(asyncGetOrdersAction.pending, (state: any) => {
                state.isLoading = true;
            })
            .addCase(asyncGetOrdersAction.rejected, (state: any, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(asyncGetOrderAction.fulfilled, (state: ordersState, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.error = '';
                state.order = action.payload;
            })
            .addCase(asyncGetOrderAction.pending, (state: ordersState) => {
                state.isLoading = true;
            })
            .addCase(asyncGetOrderAction.rejected, (state: ordersState, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(asyncUpdateOrderAction.fulfilled, (state: any, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.error = '';
                state.order = action.payload;
                toast.success("Order successfully updated", tostSetting);
            })
            .addCase(asyncUpdateOrderAction.pending, (state: any) => {
                state.isLoading = true;
            })
            .addCase(asyncUpdateOrderAction.rejected, (state: any, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(asyncDeleteOrderAction.fulfilled, (state: any, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.error = '';
                state.orders = state.orders.filter((item: IOrder) => item.id !== action.payload.id);
                toast.success("Order successfully deleted", tostSetting);
            })
            .addCase(asyncDeleteOrderAction.pending, (state: any) => {
                state.isLoading = true;
            })
            .addCase(asyncDeleteOrderAction.rejected, (state: any, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.error = action.payload;
                toast.error("Can't delete order", tostSetting);
            })

            .addCase(asyncGetGlobalUnreadCountAction.fulfilled, (state: ordersState, action: PayloadAction<number>) => {
                state.globalUnreadCount = action.payload;
            });
    }
});

export const {
    updateOrder,
    setCurrentCode,
    setSelectedOrders,
    setSelectedCount,
    setCurrentPageOrders,
    resetOrders,
    setStatusFilter,
    setCurrencyFilter,
    setMsgStatusFilter,
    incrementUnreadCount,
    clearUnreadCount,
    setGlobalUnreadCount,
    incrementGlobalUnreadCount,
    decreaseGlobalUnreadCount,
    addNewOrder
} = ordersSlice.actions;

export default ordersSlice.reducer;